import { faAngleDown, faChartLine, faHouse, faHandshake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";

export default function SellerGuide() {
  const [open, setOpen] = useState(
    new Array(constants.SELLING_GUIDE.length).fill(false)
  );

  const seoConfig = pageSEO.sellerGuide();

  const toggleAccordion = (index) => {
    const temp = [...open];
    temp[index] = !temp[index];
    setOpen(temp);
  };

  return (
    <>
      <SEO {...seoConfig} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#faf8f4]">
        <div className="absolute right-0 top-0 h-[600px] w-[600px] rounded-full bg-[#e7dfd3] blur-[140px]" />
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-[1400px] px-6 pt-40 pb-20 lg:px-16"
        >
          <motion.p
            variants={fadeUp}
            className="uppercase tracking-[0.5em] text-sm text-gray-600"
          >
            Home Selling Guide
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="mt-6 font-heading2 text-6xl leading-none text-neutral-800 lg:text-7xl"
          >
            Sell With
            <br />
            Confidence
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-2xl text-lg leading-9 text-gray-700"
          >
            Discover the strategies, preparation steps, and expert guidance needed
            to position your property for success and achieve the best possible results.
          </motion.p>
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="bg-[#fffdfa] py-28">
        <div className="mx-auto w-full lg:max-w-[1200px] px-6 lg:px-16">
          <div className="grid gap-16 grid-cols-1 lg:grid-cols-[350px_1fr]">

            {/* LEFT SIDE */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className=""
            >
              <div className="sticky top-28 border border-accent/50 bg-white p-10 shadow-lg">

                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-white">
                  <FontAwesomeIcon
                    icon={faChartLine}
                    className="text-2xl"
                  />
                </div>

                <h3 className="mt-8 font-serif text-4xl leading-tight text-neutral-800">
                  Maximize
                  <br />
                  Your Value
                </h3>

                <p className="mt-6 leading-8 text-gray-600">
                  Learn how preparation, presentation, and strategic marketing can
                  help your home stand out and achieve exceptional results.
                </p>

                <div className="mt-10 space-y-6 border-t border-[#ebe4db] pt-8">

                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20">
                      <FontAwesomeIcon
                        icon={faHouse}
                        className="text-accent"
                      />
                    </div>

                    <span className="text-sm uppercase tracking-[0.25em] text-gray-600">
                      Prepare Your Home
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20">
                      <FontAwesomeIcon
                        icon={faHandshake}
                        className="text-accent"
                      />
                    </div>

                    <span className="text-sm uppercase tracking-[0.25em] text-gray-600">
                      Complete Your Sale
                    </span>
                  </div>

                </div>

              </div>
            </motion.div>

            {/* ACCORDION */}
            <div className="space-y-8">
              {constants.SELLING_GUIDE.map((guide, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className={`relative overflow-hidden border border-accent/50 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
                >
                  {/* Decorative Corner */}
                  <div className="absolute right-6 top-6 h-10 w-10 border-r border-t border-accent/70" />

                  <button
                    onClick={() => toggleAccordion(index)}
                    className="group flex w-full items-center justify-between gap-6 px-5 lg:px-10 py-9 text-left transition-colors duration-300"
                  >
                    <div className="flex items-center gap-6">

                      {/* Optional icon */}
                      <div className="flex h-10 w-10 md:h-14 md:w-14 items-center justify-center rounded-full bg-accent/20 text-accent shrink-0">
                        <FontAwesomeIcon icon={faHouse} />
                      </div>

                      <div>
                        <p className="mb-2 text-xs uppercase tracking-[0.3em] text-accent/70">
                          Step {index + 1}
                        </p>

                        <h2
                          className={`font-serif text-xl md:text-3xl leading-tight transition-colors duration-300 text-gray-700
                          `}
                        >
                          {guide.heading}
                        </h2>
                      </div>
                    </div>

                    {/* Expand Button */}
                    <div
                      className={`flex h-10 w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-full border border-accent/50 transition-all duration-300 mr-5 lg:mr-0 ${open[index]
                        ? "bg-[#8b8172] text-white"
                        : "text-[#8b8172] group-hover:bg-[#8b8172] group-hover:text-white"
                        }
                    `}
                    >
                      <FontAwesomeIcon
                        icon={faAngleDown}
                        className={`transition-transform duration-300 ${open[index] ? "rotate-180" : ""
                          }`}
                      />
                    </div>
                  </button>

                  {/* Accordion Content */}
                  <div
                    className={`grid overflow-hidden transition-all duration-500 ease-in-out ${open[index]
                      ? "grid-rows-[1fr]"
                      : "grid-rows-[0fr]"
                      }`}
                  >
                    <div className="overflow-hidden">
                      <div className="mx-10 border-t border-gray-200 pb-10 pt-8">
                        <div
                          className="leading-8 text-gray-600"
                          dangerouslySetInnerHTML={{
                            __html: guide.text,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}